// מותגים של לקוח: רשימה, ערכה נוכחית, עריכה כגרסה חדשה, היסטוריה ו-diff בין גרסאות.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BrandKit } from '@bb/schema';
import { api } from '../../api';
import { Empty, ErrorBox, Field, Loading, Modal, fmtDate, useLoad, useMe } from '../ui';
import { DiffList, KIT_FIELD_LABELS, useAction } from './common';
import { BrandKitEditor, BrandKitView, normalizeKit } from './BrandKitEditor';
import { useClientAssets } from './AssetLibrary';

export interface BrandRow { id: string; name: string; status: string; current_version_id: string; current_version: number; created_at: string }
interface BrandFull { brand: BrandRow & { client_id: string }; current: { id: string; version: number; kit: BrandKit; note: string; created_at: string }; versions: { id: string; version: number; note: string; source: string | null; created_at: string }[] }

export function BrandPanel({ clientId, brands, onChanged }: { clientId: string; brands: BrandRow[]; onChanged: () => void }) {
  const me = useMe();
  const canEdit = me.role !== 'viewer';
  const [selected, setSelected] = useState<string | null>(brands[0]?.id ?? null);
  const [creating, setCreating] = useState(false);
  useEffect(() => { if (!selected && brands[0]) setSelected(brands[0].id); }, [brands, selected]);
  return (
    <div className="stack">
      <div className="row between">
        <div className="row">
          {brands.length > 0 && (
            <Field label="מותג / פרויקט">
              <select value={selected ?? ''} onChange={(e) => setSelected(e.target.value)}>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name} (גרסה {b.current_version})</option>)}
              </select>
            </Field>
          )}
        </div>
        <div className="row">
          {canEdit && <Link className="btn" to={`/brand-import?clientId=${clientId}`}>חילוץ שפה גרפית מאתר / PDF</Link>}
          {canEdit && <button className="btn primary" onClick={() => setCreating(true)}>מותג חדש</button>}
        </div>
      </div>
      {brands.length === 0 && <Empty>ללקוח אין מותגים עדיין. צרו מותג ידנית או חלצו שפה גרפית מאתר / PDF.</Empty>}
      {selected && <BrandDetail key={selected} brandId={selected} clientId={clientId} canEdit={canEdit} onChanged={onChanged} />}
      {creating && <NewBrandModal clientId={clientId} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setSelected(id); onChanged(); }} />}
    </div>
  );
}

function BrandDetail({ brandId, clientId, canEdit, onChanged }: { brandId: string; clientId: string; canEdit: boolean; onChanged: () => void }) {
  const { data, error, loading, setData } = useLoad<BrandFull>(`/api/brands/${brandId}`);
  const { data: assets, reload: reloadAssets } = useClientAssets(clientId, { status: 'all' });
  const assetMap = useMemo(() => Object.fromEntries((assets ?? []).map((a) => [a.id, a])), [assets]);
  const [editing, setEditing] = useState(false);
  const save = useAction();
  const [diffSel, setDiffSel] = useState<{ a: string | null; b: string | null }>({ a: null, b: null });
  const [diff, setDiff] = useState<{ from: number; to: number; changes: { field: string; before: unknown; after: unknown }[] } | null>(null);
  const diffAct = useAction();

  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  const onSave = async (kit: BrandKit, note: string) => {
    const r = await save.run(() => api.post<BrandFull>(`/api/brands/${brandId}/versions`, { kit, note }), 'נשמרה גרסת מותג חדשה');
    if (r) { setData(r); setEditing(false); onChanged(); reloadAssets(); }
  };
  const loadDiff = async () => {
    if (!diffSel.a || !diffSel.b) return;
    const r = await diffAct.run(() => api.get(`/api/brand-versions/${diffSel.a}/diff/${diffSel.b}`));
    if (r) setDiff(r);
  };

  return (
    <div className="grid cols-2" style={{ alignItems: 'start' }}>
      <section className="card" aria-labelledby="kit-h">
        <div className="row between">
          <h2 id="kit-h" style={{ margin: 0 }}>{data.brand.name} <span className="badge info">גרסה {data.current.version}</span></h2>
          {canEdit && !editing && <button className="btn primary" onClick={() => setEditing(true)}>עריכה (גרסה חדשה)</button>}
        </div>
        <div className="small muted">עודכן {fmtDate(data.current.created_at)}{data.current.note ? ` · ${data.current.note}` : ''}</div>
        <div style={{ marginTop: 10 }}>
          {editing
            ? <BrandKitEditor clientId={clientId} initial={normalizeKit(data.current.kit)} busy={save.busy} error={save.error} onSave={onSave} onCancel={() => setEditing(false)} />
            : <BrandKitView kit={data.current.kit} assets={assetMap} />}
        </div>
      </section>
      <section className="card" aria-labelledby="ver-h">
        <h2 id="ver-h" style={{ marginTop: 0 }}>היסטוריית גרסאות</h2>
        <p className="small muted">בחרו שתי גרסאות להשוואה (מ־ / אל).</p>
        <table className="table">
          <thead><tr><th>גרסה</th><th>הערה</th><th>מ־</th><th>אל</th></tr></thead>
          <tbody>
            {data.versions.map((v) => (
              <tr key={v.id}>
                <td><strong>{v.version}</strong>{v.id === data.current.id && <> <span className="badge ok">נוכחית</span></>}<div className="small muted">{fmtDate(v.created_at)}</div></td>
                <td className="small">{v.note || '—'}{v.source && <div className="muted">מקור: {v.source}</div>}</td>
                <td><input type="radio" name="diff-a" aria-label={`השוואה מגרסה ${v.version}`} checked={diffSel.a === v.id} onChange={() => setDiffSel((s) => ({ ...s, a: v.id }))} /></td>
                <td><input type="radio" name="diff-b" aria-label={`השוואה לגרסה ${v.version}`} checked={diffSel.b === v.id} onChange={() => setDiffSel((s) => ({ ...s, b: v.id }))} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ marginTop: 8 }}>
          <button className="btn" onClick={loadDiff} disabled={!diffSel.a || !diffSel.b || diffSel.a === diffSel.b || diffAct.busy}>{diffAct.busy ? 'משווה…' : 'הצגת הבדלים'}</button>
        </div>
        <ErrorBox error={diffAct.error} />
        {diff && (
          <div style={{ marginTop: 10 }} aria-live="polite">
            <h3>גרסה {diff.from} ← גרסה {diff.to}</h3>
            <DiffList changes={diff.changes} labels={KIT_FIELD_LABELS} />
          </div>
        )}
      </section>
    </div>
  );
}

function NewBrandModal({ clientId, onClose, onCreated }: { clientId: string; onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const { busy, error, run } = useAction();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await run(() => api.post<BrandFull>('/api/brands', { clientId, name }), 'המותג נוצר');
    if (r) onCreated(r.brand.id);
  };
  return (
    <Modal title="מותג / פרויקט חדש" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <Field label="שם המותג *"><input required value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
        <div className="small muted">המותג נוצר עם גרסה 1 ריקה. אחר כך ניתן למלא את הערכה ידנית או לחלץ מאתר / PDF.</div>
        <ErrorBox error={error} />
        <div className="row"><button className="btn primary" disabled={busy || !name.trim()}>{busy ? 'יוצר…' : 'יצירה'}</button><button type="button" className="btn" onClick={onClose}>ביטול</button></div>
      </form>
    </Modal>
  );
}
